import { useState } from 'react';
import { useNavigation } from '../../../hooks/useNavigation';
import { useInspectorStore } from '../../../stores/inspectorStore';
import { useUploaderStore } from '../../../stores/uploaderStore';
import styled from 'styled-components';
import { Card, Spinner, token } from '@bynder/design-system';
import { IconCleanCard } from '@bynder/icons';
import { CroppedImage } from '@bynder/react-image-cropper-lib';
import { ActiveSplits, useReleaseToggle } from '../../../helpers/releaseToggle';
import { getTypographyWeight } from '../../shared/styled-components/constants';
import { addQuotes, toPx } from '../../guides/Inspector/helpers';

export interface CardWidgetCardProps {
  isWidgetActive: boolean | undefined;
  cardImageUrl: string | undefined;
  cardLink: string;
  cardId: string;
  openNewTab: boolean;
  index: number | undefined;
  isEditMode: boolean;
  isPreviewMode?: boolean;
  titleColor: string;
  cardTitle: string;
  cardDescription: string;
  descriptionColor: string;
  borderColor: string;
  $borderRadius: string;
  $titleTypography: {
    fontFamily: string;
    fontStyle: string;
    fontSize: number;
  };
  $descriptionTypography: {
    fontFamily: string;
    fontStyle: string;
    fontSize: number;
  };
}
export const CardWidgetCard: React.FC<CardWidgetCardProps> = ({
  index,
  isWidgetActive,
  isEditMode,
  cardTitle,
  isPreviewMode = false,
  cardDescription,
  titleColor,
  descriptionColor,
  borderColor,
  cardImageUrl,
  cardLink,
  cardId,
  openNewTab,
  $borderRadius,
  $titleTypography,
  $descriptionTypography,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const { navigate } = useNavigation();

  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const handleEditClick = () => {
    setWidgetSettings({ widgetIndex: index });
  };

  const isUploading = useUploaderStore.use
    .uploadingImages()
    .some(image => image.id === cardId);
  const handleClick = e => {
    if (!isPreviewMode) {
      e.preventDefault();
      if (isEditMode) handleEditClick();
      else navigate(cardLink, openNewTab);
    }
  };

  const { data: isCropperSplitActive } = useReleaseToggle(
    ActiveSplits.imageCropperRollout,
  );

  const cardComponent = (
    <StyledCard
      data-testid="card-widget-card"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      $titleColor={titleColor}
      $subtitleColor={descriptionColor}
      $borderColor={borderColor}
      $borderRadius={$borderRadius}
      $titleTypography={$titleTypography}
      $descriptionTypography={$descriptionTypography}
    >
      <Card
        isSelected={!isEditMode && isHovering}
        noMinWidth
        variant="clean"
        onClick={handleClick}
      >
        <Card.Frame>
          {cardImageUrl ? (
            isCropperSplitActive ? (
              <CroppedImage src={cardImageUrl} absolutePositioning />
            ) : (
              <Card.Images>
                <Card.Img src={cardImageUrl} />
              </Card.Images>
            )
          ) : (
            <Card.IconPlaceholder>
              {isUploading ? <Spinner /> : <IconCleanCard />}
            </Card.IconPlaceholder>
          )}
        </Card.Frame>

        <Card.Header
          isNotTruncated
          title={cardTitle}
          detail={cardDescription}
        />
      </Card>
    </StyledCard>
  );

  if (isEditMode && index === widgetSettings?.widgetIndex && isWidgetActive)
    return (
      <div
        style={{
          outline: `3px solid ${token.brandPrimary500}`,
        }}
      >
        {cardComponent}
      </div>
    );

  return cardComponent;
};

const StyledCard = styled.div<{
  $borderColor?: string;
  $titleColor?: string;
  $subtitleColor?: string;
  $borderRadius?: string;
  $titleTypography?: {
    fontFamily: string;
    fontStyle: string;
    fontSize: number;
  };
  $descriptionTypography?: {
    fontFamily: string;
    fontStyle: string;
    fontSize: number;
  };
}>`
  div[class*='Container-'] {
    background-color: transparent;
  }
  div[class*='Children-'],
  div[class*='FrameStyled-'] {
    ${({ $borderRadius }) =>
      $borderRadius && `border-radius: ${toPx($borderRadius)}`};
  }
  div[class*='FocusRing-'] {
    ${({ $borderColor }) =>
      $borderColor && `border: 2px solid ${$borderColor}`};
    ${({ $borderRadius }) =>
      $borderRadius && `border-radius: ${toPx($borderRadius)}`};
  }
  div[class*='Title-'] {
    ${({ $titleTypography }) =>
      $titleTypography?.fontFamily &&
      `font-family: ${addQuotes($titleTypography.fontFamily)}`};
    ${({ $titleTypography, theme }) =>
      $titleTypography?.fontStyle &&
      `font-weight: ${getTypographyWeight(theme, $titleTypography.fontFamily, $titleTypography.fontStyle)}`};
    ${({ $titleTypography }) =>
      $titleTypography?.fontSize &&
      `font-size: ${toPx($titleTypography.fontSize)}`};
    ${({ $titleColor }) => $titleColor && `color: ${$titleColor}`};
  }
  div[class*='Detail-'] {
    ${({ $subtitleColor }) => $subtitleColor && `color: ${$subtitleColor}`};
    ${({ $descriptionTypography }) =>
      $descriptionTypography?.fontFamily &&
      `font-family: ${addQuotes($descriptionTypography.fontFamily)}`};
    ${({ $descriptionTypography, theme }) =>
      $descriptionTypography?.fontStyle &&
      `font-weight: ${getTypographyWeight(theme, $descriptionTypography.fontFamily, $descriptionTypography.fontStyle)}`};
    ${({ $descriptionTypography }) =>
      $descriptionTypography?.fontSize &&
      `font-size: ${toPx($descriptionTypography.fontSize)}`};
  }
  div[class*='Overlay-'] {
    ${({ $borderRadius }) =>
      $borderRadius && `border-radius: ${toPx($borderRadius)}`};
  }
`;
